import React, { useMemo, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Spinner from '@shared/components/spinner/Spinner';
import { knowledgeBaseApi } from '@shared/services/apiService/knowledgeBaseApi';
import { getKnowledgeBaseErrorMessageKey } from '@features/Admin/KnowledgeBase/model/error-mapper';
import { mapFileResponseToKnowledgeBaseFile } from '@features/Admin/KnowledgeBase/model/file-mapper';

import { AgentFileCard } from './AgentFileCard';
import { AgentFileDetailsDialog } from './AgentFileDetailsDialog';
import { getAvailableAgentFiles } from './agent-files';

type Props = {
  campaignId: string;
  agentId: string;
};

export const AgentFilesSettingsPanel: React.FC<Props> = ({ campaignId, agentId }) => {
  const { t } = useTranslation();
  const [detailsFileId, setDetailsFileId] = useState<string | null>(null);
  const { data, error, isFetching, isLoading } =
    knowledgeBaseApi.useCampaignFileListControllerListQuery(
      { id: campaignId },
      {
        skip: !campaignId || !agentId,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
      },
    );

  const files = useMemo(
    () => getAvailableAgentFiles((data ?? []).map(mapFileResponseToKnowledgeBaseFile), agentId),
    [agentId, data],
  );
  const detailsFile = useMemo(
    () => files.find((file) => file.id === detailsFileId),
    [detailsFileId, files],
  );

  if (isLoading && !data) return <Spinner />;

  if (error && !data) {
    return (
      <Typography variant="body2" color="error.main" role="alert">
        {t(getKnowledgeBaseErrorMessageKey(error))}
      </Typography>
    );
  }

  return (
    <Stack gap={1.5}>
      {isFetching && data && (
        <CircularProgress size={18} aria-label={t('campaignPage.knowledgeBase.refreshing')} />
      )}
      {files.length > 0 ? (
        files.map((file) => (
          <AgentFileCard key={file.id} file={file} onDetails={setDetailsFileId} />
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('adminMainPage.agent.advancedSettingsFiles.empty')}
        </Typography>
      )}
      {detailsFile && (
        <AgentFileDetailsDialog
          key={detailsFile.id}
          file={detailsFile}
          onClose={() => setDetailsFileId(null)}
        />
      )}
    </Stack>
  );
};
